/* eslint-disable no-console */
/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Service worker registration for web
 */

type Config = {
	onUpdate: (registration: ServiceWorkerRegistration, metadata: { version: string }) => void;
};

/**
 * Approve update and reload the page
 * @returns void
 */
const approveUpdate = async (): Promise<void> => {
	setTimeout(() => window.location.reload(), 1000);
	if ('serviceWorker' in navigator) {
		try {
			const registration = await navigator.serviceWorker.ready;
			if (registration.waiting) {
				registration.waiting.postMessage('SKIP_WAITING');
			}
		} catch (error: AnyType) {
			console.error(error.message);
		}
		window.location.reload();
	}
};

const invokeServiceWorkerUpdateFlow = async (registration: ServiceWorkerRegistration, config: Config) => {
	try {
		await fetch('./manifest.json')
			.then((res) => res.json())
			.then((manifest) => {
				config.onUpdate(registration, { version: manifest?.version || RELEASE_VERSION });
			});
	} catch (err) {
		console.log('No internet connection found. App is running in offline mode.', err);
		config.onUpdate(registration, { version: RELEASE_VERSION });
	}
};

const registerValidSW = async (swUrl: string, config: Config) => {
	try {
		await navigator.serviceWorker.register(swUrl).then(async (registration) => {
			// Check for updates at start.
			await registration.update();
			// Check for updates every min.
			setInterval(() => {
				void registration.update().catch((error) => console.warn(error));
			}, 1000 * 60);

			// If there is a waiting Service Worker at start, approve without prompt
			if (registration.waiting) {
				approveUpdate();
			}

			// Handle update found event to invoke the update flow
			registration.onupdatefound = () => {
				const installingWorker = registration.installing;
				if (installingWorker) {
					installingWorker.onstatechange = () => {
						if (installingWorker.state === 'installed') {
							invokeServiceWorkerUpdateFlow(registration, config);
						}
					};
				}
			};
		});
	} catch (error) {
		console.error('Error during service worker registration :', error);
	}
};

const register = (config: Config): void => {
	if ('serviceWorker' in navigator) {
		globalThis.addEventListener('load', () => {
			const swUrl = '/sw.js';

			registerValidSW(swUrl, config)
				.then(() => {
					console.log('Service worker registered successfully.');
				})
				.catch((error) => {
					console.error('Error during service worker registration:', error);
				});
		});
	}
};

const unregister = async (): Promise<void> => {
	if ('serviceWorker' in navigator) {
		try {
			const registration = await navigator.serviceWorker.ready;
			await registration.unregister();
		} catch (error: AnyType) {
			console.error(error.message);
		}
	}
};

export { register, unregister, approveUpdate };
